/**
 * Panduan Page - Complete User Guide for All Menus
 * Target: Team understand 80% after reading this guide
 * Version 2.0 - Enhanced with 25 FAQs and detailed guides
 */
import { useState } from 'react';
import { 
  BookOpen, Home, Zap, BarChart3, Brain, Bell, TrendingUp, CreditCard, Wallet,
  PieChart, Upload, Target, Building2, FileText, Award, Package, UserCog, Users,
  Activity, UserPlus, ChevronDown, ChevronRight, CheckCircle, Lightbulb, 
  PlayCircle, AlertTriangle, HelpCircle, Star, Crown, DollarSign, Briefcase,
  Clock, Calendar, Shield, Settings, Database, RefreshCw, Download, Search,
  Eye, Edit, Trash2, Plus, Filter, ArrowRight, Info, AlertCircle, Scale, Boxes
} from 'lucide-react';

const GuideSection = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-100 border-gray-300 text-gray-700'
  };
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 flex items-center justify-between ${colorClasses[color]} border-b`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <span className="font-bold text-lg">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

const MenuCard = ({ title, icon: Icon, description, howToUse, tips, keyFeatures, path, videoTime }) => (
  <div className="bg-gray-50 rounded-xl p-5 mb-4 border border-gray-100">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-white rounded-lg shadow-sm">
          <Icon className="w-5 h-5 text-gray-700" />
        </div>
        <div>
          <h4 className="font-bold text-gray-800">{title}</h4>
          <p className="text-xs text-gray-500">{path}</p>
        </div>
      </div>
      {videoTime && (
        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full flex items-center gap-1">
          <Clock className="w-3 h-3" /> {videoTime}
        </span>
      )}
    </div>
    
    <p className="text-sm text-gray-600 mb-4 leading-relaxed">{description}</p>
    
    {/* Key Features */}
    {keyFeatures && keyFeatures.length > 0 && (
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 flex items-center gap-1 mb-2">
          <Star className="w-3 h-3" /> Key Features:
        </p>
        <div className="grid grid-cols-2 gap-2">
          {keyFeatures.map((feature, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-purple-50 px-2 py-1 rounded">
              <CheckCircle className="w-3 h-3 text-purple-500" />
              {feature}
            </div>
          ))}
        </div>
      </div>
    )}
    
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-blue-600 flex items-center gap-1 mb-2">
          <PlayCircle className="w-3 h-3" /> Cara Guna (Step-by-Step):
        </p>
        <ol className="text-xs text-gray-600 space-y-2 ml-1">
          {howToUse.map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">{i+1}</span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </div>
      
      {tips && tips.length > 0 && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
          <p className="text-xs font-semibold text-amber-600 flex items-center gap-1 mb-2">
            <Lightbulb className="w-3 h-3" /> Pro Tips:
          </p>
          <ul className="text-xs text-amber-700 space-y-1">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

const FAQItem = ({ question, answer, category }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const categoryColors = {
    general: 'bg-gray-100 text-gray-600',
    finance: 'bg-emerald-100 text-emerald-600',
    hr: 'bg-blue-100 text-blue-600',
    technical: 'bg-purple-100 text-purple-600',
    troubleshoot: 'bg-red-100 text-red-600'
  };
  
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-3 flex items-start gap-3 text-left hover:bg-gray-50 px-2 rounded"
      >
        <div className={`px-2 py-0.5 rounded text-xs font-medium ${categoryColors[category]}`}>
          {category === 'general' ? 'Umum' : 
           category === 'finance' ? 'Kewangan' : 
           category === 'hr' ? 'HR' : 
           category === 'technical' ? 'Teknikal' : 'Masalah'}
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-800 text-sm">{question}</p>
          {isOpen && (
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{answer}</p>
          )}
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />}
      </button>
    </div>
  );
};

const GuidePage = () => {
  const [faqFilter, setFaqFilter] = useState('all');
  
  const faqs = [
    // GENERAL (5)
    { category: 'general', question: 'Macam mana nak mula guna sistem ini?', answer: 'Mula dengan Quick Start di atas: 1) Lihat Overview untuk gambaran keseluruhan, 2) Set goals di Command Center, 3) Key in data Revenue/Expenses, 4) Guna AI Analysis untuk insights. Baca panduan setiap menu untuk details.' },
    { category: 'general', question: 'Siapa yang patut guna sistem ini?', answer: 'Director/CEO untuk strategic decisions, Finance team untuk kewangan, HR team untuk staff management, Operations untuk daily tracking. Setiap role ada menu yang relevan.' },
    { category: 'general', question: 'Data dalam sistem ni selamat ke?', answer: 'Ya, data disimpan dalam MongoDB dengan encryption. Login menggunakan Google OAuth. Hanya email yang didaftarkan boleh access. Setiap role ada akses berbeza.' },
    { category: 'general', question: 'Boleh access dari phone?', answer: 'Ya, sistem ini responsive dan boleh diakses dari mobile browser. Malah, anda boleh install sebagai PWA (Progressive Web App) untuk pengalaman seperti native app.' },
    { category: 'general', question: 'Macam mana nak train team guna sistem?', answer: 'Minta semua team baca Menu Panduan ini dulu (20 minit). Kemudian buat hands-on session di mana setiap orang cuba key in data dalam bahagian masing-masing. Refer FAQ jika ada soalan.' },
    
    // FINANCE (7)
    { category: 'finance', question: 'Macam mana nak export data untuk accountant?', answer: 'Setiap page ada button Export (biasanya di header atau footer table). Klik untuk download dalam format CSV/Excel. Accountant boleh guna untuk buat akaun rasmi.' },
    { category: 'finance', question: 'Kenapa Cash Runway tunjuk merah?', answer: 'Cash runway merah bermaksud cash anda hanya cukup kurang dari 3 bulan. Ini KRITIKAL! Perlu: 1) Cut unnecessary expenses, 2) Speed up collection, 3) Consider financing options. Jangan abaikan alert ini.' },
    { category: 'finance', question: 'Revenue dan Channel Revenue berbeza ke?', answer: 'Sama fungsi tapi berbeza tracking. Revenue (main) untuk income umum. Channel Revenue untuk track income by specific channel (FTC, Goldcast, dll). Kedua-duanya auto-masuk Total Revenue.' },
    { category: 'finance', question: 'Bank Import support format apa?', answer: 'CSV dari Maybank, CIMB, RHB, Public Bank. Export dari online banking dalam format CSV (bukan PDF). Sistem akan auto-detect format dan categorize transactions.' },
    { category: 'finance', question: 'Macam mana sistem calculate profit margin?', answer: 'Profit Margin = (Total Revenue - Total Expenses) / Total Revenue × 100%. Healthy margin biasanya 15-30%. Di bawah 10% perlu review pricing atau cut costs.' },
    { category: 'finance', question: 'Boleh track multiple bank accounts?', answer: 'Ya, upload statement dari setiap bank. Sistem akan categorize semua transactions. Cash balance dalam sistem adalah gabungan yang anda key in di Cashflow page.' },
    { category: 'finance', question: 'Depreciation asset dikira macam mana?', answer: 'Sistem guna Straight-Line Method: (Purchase Value - Salvage Value) / Useful Life. Contoh: Laptop RM5000, useful life 5 tahun = RM1000/tahun depreciation.' },
    
    // HR (6)
    { category: 'hr', question: 'EPF, SOCSO, EIS dikira automatic ke?', answer: 'Ya, 100% automatic bila anda masukkan Basic Salary. EPF Employee: 11%, EPF Employer: 13%, SOCSO & EIS: Based on jadual rasmi. PCB dikira based on salary bracket.' },
    { category: 'hr', question: 'Macam mana nak import attendance dari Excel?', answer: 'Pergi HR → Attendance → Import. Upload file Excel/CSV dengan column: Staff Name, Date, Time In, Time Out. Atau upload PDF Timesheet Report (auto-parse dengan analytics: total jam, on time, lewat > 9:30 AM, auto clock out, breakdown mingguan).' },
    { category: 'hr', question: 'Flight Risk tu apa maksudnya?', answer: 'Flight Risk = kemungkinan staff resign. Sistem analyze: tenure (2-5 tahun highest risk), salary vs average, active warnings, no promotion. High risk staff perlu retention action.' },
    { category: 'hr', question: 'Staff ROI negative bermaksud apa?', answer: 'ROI negative = kos staff lebih tinggi dari revenue contribution. Bukan bermaksud staff tak bagus, tapi perlu review: 1) Productivity, 2) Training needs, 3) Role suitability.' },
    { category: 'hr', question: 'Boleh set approval workflow untuk leave?', answer: 'Ya, bila staff apply leave, status jadi "Pending". HR/Manager boleh Approve atau Reject dari HR → Leave page. Staff akan nampak status updated.' },
    { category: 'hr', question: 'Macam mana nak track staff training?', answer: 'HR → Training tab. Add training record dengan: Staff name, Training title, Date, Provider, Cost, Certificate (attach). Boleh track siapa sudah/belum attend mandatory training.' },
    
    // TECHNICAL (8) - Updated with SQL & Security
    { category: 'technical', question: 'Data auto-save ke atau perlu manual save?', answer: 'Auto-save bila anda klik Submit/Save button. Tidak ada auto-save semasa typing. Pastikan klik Save sebelum tutup page.' },
    { category: 'technical', question: 'Boleh integrate dengan sistem lain?', answer: 'Ya! Sistem ini sudah integrate dengan SQL Accounting. Data dari SQL akan auto-sync ke Dashboard. Untuk integration lain, hubungi admin.' },
    { category: 'technical', question: 'Data backup macam mana?', answer: 'Database automatic backup oleh platform. Untuk backup manual, export data dari setiap module ke CSV. Simpan di cloud storage sebagai tambahan.' },
    { category: 'technical', question: 'Berapa ramai user boleh guna serentak?', answer: 'Tiada limit untuk concurrent users. Sistem cloud-based dan scalable. Namun untuk data integrity, elakkan dua orang edit record yang sama pada masa yang sama.' },
    { category: 'technical', question: 'SQL Accounting sync macam mana?', answer: 'Pergi ke SQL Accounting → Overview & Sync → Klik "Sync Now". Data akan auto-populate ke Revenue, Expenses, Customer Analytics, dan Inventory Analytics.' },
    { category: 'technical', question: 'Macam mana nak login ke sistem?', answer: 'Klik "Log masuk dengan Google" di login page. Pilih akaun Google yang didaftarkan. Hanya email yang didaftarkan dalam sistem boleh login.' },
    { category: 'technical', question: 'Kenapa saya tak nampak semua menu?', answer: 'Menu dipaparkan berdasarkan role anda. DIRECTOR/FINANCE nampak semua. HR hanya nampak menu HR. Check dengan admin jika perlu akses tambahan.' },
    
    // TROUBLESHOOT (5)
    { category: 'troubleshoot', question: 'Page loading lambat atau stuck, apa nak buat?', answer: '1) Refresh page (Ctrl+R), 2) Clear browser cache, 3) Cuba browser lain (Chrome recommended), 4) Check internet connection. Jika masih bermasalah, report ke admin.' },
    { category: 'troubleshoot', question: 'Data yang saya key in hilang, macam mana?', answer: 'Pastikan anda klik Save/Submit sebelum tutup. Jika sudah save tapi hilang: 1) Refresh page, 2) Check filter settings, 3) Check date range. Data tidak delete sendiri.' },
    { category: 'troubleshoot', question: 'Chart/Graph tidak display, kenapa?', answer: 'Biasanya sebab tiada data untuk period yang dipilih. Cuba: 1) Tukar date range, 2) Pastikan ada data dalam sistem, 3) Refresh page. Chart perlu minimum 1 data point.' },
    { category: 'troubleshoot', question: 'SQL Accounting tunjuk error, apa nak buat?', answer: 'Error "Unauthorized" bermakna API credentials belum active. Hubungi admin untuk verify. Error "Connection" bermakna server SQL tidak dapat dicapai.' },
    { category: 'troubleshoot', question: 'Tak boleh login walaupun email betul?', answer: 'Email mesti didaftarkan dalam sistem oleh admin. Hubungi admin untuk tambah email anda. Pastikan guna akaun Google yang betul.' },
  ];
  
  const filteredFaqs = faqFilter === 'all' ? faqs : faqs.filter(f => f.category === faqFilter);

  return (
    <div className="space-y-6" data-testid="guide-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <BookOpen className="w-10 h-10" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Panduan Sistem</h1>
            <p className="text-red-200 text-sm mt-1">Panduan lengkap untuk faham dan guna sistem dengan berkesan</p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">5</p>
            <p className="text-xs text-red-200">Kategori Menu</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">25+</p>
            <p className="text-xs text-red-200">Modules</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">30+</p>
            <p className="text-xs text-red-200">FAQs</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">20</p>
            <p className="text-xs text-red-200">Min Baca</p>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Search className="w-4 h-4" /> Quick Navigation
        </h3>
        <div className="flex flex-wrap gap-2">
          <a href="#quickstart" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700">Quick Start</a>
          <a href="#executive" className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg text-sm text-amber-700">Executive</a>
          <a href="#finance" className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg text-sm text-emerald-700">Finance</a>
          <a href="#hr" className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 rounded-lg text-sm text-blue-700">HR & Operations</a>
          <a href="#integration" className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 rounded-lg text-sm text-purple-700">Data Integration</a>
          <a href="#sql-accounting" className="px-3 py-1.5 bg-cyan-100 hover:bg-cyan-200 rounded-lg text-sm text-cyan-700">SQL Accounting</a>
          <a href="#security" className="px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg text-sm text-red-700">Security & Roles</a>
          <a href="#faq" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700">FAQ (25+ Soalan)</a>
        </div>
      </div>

      {/* Quick Start */}
      <div id="quickstart" className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
          <Star className="w-5 h-5 text-amber-500" /> Quick Start - Mula Dalam 5 Minit
        </h2>
        <p className="text-sm text-gray-600 mb-4">Ikuti 4 langkah ini untuk mula guna sistem dengan efektif</p>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="font-bold text-blue-600">1</span>
            </div>
            <p className="text-sm font-medium text-gray-800 text-center">Lihat Overview</p>
            <p className="text-xs text-gray-500 text-center mt-1">Dashboard utama untuk gambaran keseluruhan bisnes anda</p>
            <div className="mt-2 text-center">
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Menu: Overview</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="font-bold text-emerald-600">2</span>
            </div>
            <p className="text-sm font-medium text-gray-800 text-center">Set Company Goals</p>
            <p className="text-xs text-gray-500 text-center mt-1">Tetapkan target Revenue, Profit, Staff di Command Center</p>
            <div className="mt-2 text-center">
              <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded">Menu: Command Center</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="font-bold text-amber-600">3</span>
            </div>
            <p className="text-sm font-medium text-gray-800 text-center">Key In Data</p>
            <p className="text-xs text-gray-500 text-center mt-1">Masukkan Revenue, Expenses, Staff data setiap hari/minggu</p>
            <div className="mt-2 text-center">
              <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded">Menu: Finance & HR & Staff</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="font-bold text-purple-600">4</span>
            </div>
            <p className="text-sm font-medium text-gray-800 text-center">Analisis AI</p>
            <p className="text-xs text-gray-500 text-center mt-1">Dapatkan insights dan recommendations dari AI consultant</p>
            <div className="mt-2 text-center">
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Menu: AI Analysis</span>
            </div>
          </div>
        </div>
        
        {/* Daily Routine */}
        <div className="mt-6 bg-white rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" /> Routine Harian yang Disarankan
          </h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700">Pagi (9:00 AM)</p>
                <p className="text-xs text-gray-500">Check Command Center alerts, review pending approvals</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700">Tengahari (12:00 PM)</p>
                <p className="text-xs text-gray-500">Key in morning transactions, update attendance</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-purple-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-700">Petang (5:00 PM)</p>
                <p className="text-xs text-gray-500">Complete daily entries, review dashboards</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Icon Legend */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" /> Legend - Maksud Icons & Warna
        </h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs">BACA</span>
            <span className="text-gray-600">Wajib baca</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded text-xs">NEW</span>
            <span className="text-gray-600">Feature baru</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded text-xs">AI</span>
            <span className="text-gray-600">AI-powered</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
            <span className="text-gray-600">Alert kritikal</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
            <span className="text-gray-600">Warning</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
            <span className="text-gray-600">OK / Positif</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
            <span className="text-gray-600">Info</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-400 rounded-full"></span>
            <span className="text-gray-600">Inactive</span>
          </div>
        </div>
      </div>

      {/* Executive Menu */}
      <div id="executive">
        <GuideSection title="EXECUTIVE" icon={Crown} color="amber" defaultOpen={true}>
          <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-100">
            <p className="text-sm text-amber-800">
              <strong>Untuk siapa?</strong> Director, CEO, Management Team
            </p>
            <p className="text-sm text-amber-700 mt-1">
              <strong>Tujuan:</strong> Lihat gambaran besar bisnes, buat keputusan strategik, monitor KPIs
            </p>
          </div>

          <MenuCard
            title="Overview"
            icon={Home}
            path="/overview"
            description="Dashboard utama yang memaparkan ringkasan semua data kewangan, HR, dan aset dalam satu pandangan. Ini adalah 'homepage' sistem anda."
            keyFeatures={['Real-time KPIs', 'Revenue vs Expenses', 'Cash Position', 'Staff Summary', 'Month Comparison']}
            howToUse={[
              "Buka menu Overview dari sidebar (atau klik logo)",
              "Lihat 4 KPI cards utama di atas: Revenue, Expenses, Profit, Cash Balance",
              "Check trend arrows: hijau ↑ = naik, merah ↓ = turun dari bulan lepas",
              "Scroll ke 'Comparison' section untuk bandingkan dengan bulan lepas",
              "Lihat charts untuk visual breakdown by category",
              "Check alerts panel untuk isu yang perlu perhatian"
            ]}
            tips={[
              "Jadikan Overview sebagai first page setiap pagi",
              "Perhatikan warna - merah = perlu tindakan segera",
              "Gunakan comparison untuk track improvement month-over-month"
            ]}
          />

          <MenuCard
            title="Command Center"
            icon={Zap}
            path="/command-center"
            description="Pusat kawalan eksekutif dengan Smart Alerts, Company Goals (OKR), dan Cash Flow Forecast 12 bulan. Ini adalah 'control tower' untuk strategic planning."
            keyFeatures={['Business Health Score', 'Smart Alerts', 'Goal Tracking (OKR)', 'Cash Forecast', 'AI Recommendations']}
            howToUse={[
              "Lihat Business Health Score di header (0-100). Target: >70",
              "Review Smart Alerts: 🔴 Critical (tindakan segera), 🟡 Warning (monitor), 🟢 Positive (good news)",
              "Scroll ke Goals section, klik 'Tambah Goal' untuk set target",
              "Isi: Nama goal, Target value, Category, Start/End date",
              "Pilih 'Auto-Track' jika mahu sistem update progress automatik dari data",
              "Lihat Cash Flow Forecast chart - perhatikan bila line jatuh ke negative",
              "Read AI Recommendations untuk actionable insights"
            ]}
            tips={[
              "Set minimum 3-5 company goals setiap quarter",
              "Review goals weekly - adjust jika perlu",
              "Cash runway <3 bulan = URGENT action needed",
              "Auto-track goals untuk mengurangkan manual update"
            ]}
          />

          <MenuCard
            title="Executive Dashboard"
            icon={BarChart3}
            path="/executive"
            description="Dashboard komprehensif untuk executive dengan semua KPI kewangan, HR metrics, dan asset summary dalam satu view."
            keyFeatures={['Period Selector', 'Financial KPIs', 'HR Health', 'Asset Summary', 'AI Insights']}
            howToUse={[
              "Pilih period di header: Semua / Bulan Ini / Quarter / YTD",
              "Section 1: Financial Summary - Revenue, Expenses, Profit dengan MYR conversion",
              "Section 2: Cash Position - Balance, Net Flow, Runway estimation",
              "Section 3: HR Overview - Total staff, Warnings, Pending leaves",
              "Section 4: Asset Summary - Total value, Depreciation, Maintenance alerts",
              "Scroll ke bawah untuk detailed breakdowns dan charts"
            ]}
            tips={[
              "Guna untuk persediaan board meeting atau investor presentation",
              "Screenshot sections yang penting untuk reporting",
              "Compare YTD untuk annual performance review"
            ]}
          />

          <MenuCard
            title="AI Insights"
            icon={Brain}
            path="/ai-analysis"
            description="AI Business Consultant dengan persona 30 tahun pengalaman. Beri analisis mendalam dan advice seperti consultant profesional."
            keyFeatures={['Financial Health Check', 'Trend Analysis', 'Predictive Analytics', 'Industry Benchmarking', 'Scenario Planner']}
            howToUse={[
              "Pilih jenis analisis dari dropdown: Financial Health / Revenue / Expenses / HR / Custom",
              "Untuk Custom, taip soalan anda dalam bahasa biasa. Contoh: 'Kenapa profit margin menurun?'",
              "Klik 'Analyze' dan tunggu 10-30 saat untuk AI process",
              "Baca insights yang diberikan - AI akan highlight key issues dan opportunities",
              "Cuba advanced features: Trend Analysis (pattern recognition), Predictive (forecast), Benchmarking (vs industry), Scenario Planner (what-if)",
              "Scenario Planner: Masukkan scenario seperti 'hire 5 staff' atau 'revenue drop 20%' untuk lihat impact"
            ]}
            tips={[
              "Soalan spesifik = jawapan lebih berguna",
              "AI baca SEMUA data dalam sistem - tak perlu explain background",
              "Guna Scenario Planner sebelum buat keputusan besar",
              "Run analysis weekly untuk catch issues early"
            ]}
          />
        </GuideSection>
      </div>

      {/* Finance Menu */}
      <div id="finance">
        <GuideSection title="FINANCE" icon={DollarSign} color="emerald">
          <div className="bg-emerald-50 rounded-lg p-4 mb-4 border border-emerald-100">
            <p className="text-sm text-emerald-800">
              <strong>Untuk siapa?</strong> Finance Team, Accounts, Directors
            </p>
            <p className="text-sm text-emerald-700 mt-1">
              <strong>Tujuan:</strong> Track semua duit masuk dan keluar, manage cashflow, generate reports
            </p>
          </div>

          <MenuCard
            title="Revenue"
            icon={TrendingUp}
            path="/revenue"
            description="Track semua pendapatan/income syarikat. Support multiple sources dan attachment untuk documentation."
            keyFeatures={['Multiple Sources', 'Channel Tracking', 'File Attachments', 'Trend Charts', 'Export Function']}
            howToUse={[
              "Klik button 'Tambah Revenue' (hijau) di header",
              "Isi form: Date, Amount (RM), Source (pilih dari dropdown)",
              "Source options: Affiliate Sales, Sponsorship, Consultation, Commission Referral, Others",
              "Pilih Channel jika berkaitan (optional)",
              "Add Description untuk reference",
              "Attach document (invoice/receipt) jika ada - klik 'Choose File'",
              "Klik 'Submit' untuk save",
              "Lihat graph di bawah untuk trend analysis - toggle Monthly/Quarterly/Yearly"
            ]}
            tips={[
              "Key in revenue on the same day it's received",
              "Always attach invoice untuk audit trail",
              "Categorize source correctly untuk accurate reporting",
              "Guna filter untuk cari specific transactions"
            ]}
          />

          <MenuCard
            title="Expenses"
            icon={CreditCard}
            path="/expenses"
            description="Track semua perbelanjaan dengan kategorisasi. Auto-sync ke P&L dan cashflow."
            keyFeatures={['Category System', 'File Attachments', 'Approval Workflow', 'Budget Tracking', 'Recurring Expenses']}
            howToUse={[
              "Klik 'Tambah Expense' (merah) di header",
              "Pilih Category: Staff Cost, Advertising, General Admin Expenses, Utilities, etc.",
              "Isi: Date, Amount, Description",
              "Attach receipt/invoice untuk documentation",
              "Submit - expense akan reflect dalam P&L dan Cashflow",
              "Lihat Expense Breakdown chart untuk analysis by category",
              "Guna filter untuk view specific category atau date range"
            ]}
            tips={[
              "Categorize dengan betul - affect reporting accuracy",
              "Staff Cost = semua kos berkaitan staff (bukan di Payroll)",
              "Advertising = semua marketing spend (FB Ads, Google, etc.)",
              "Attach ALL receipts untuk tax dan audit purposes"
            ]}
          />

          <MenuCard
            title="Cashflow"
            icon={Wallet}
            path="/cashflow"
            description="Monitor aliran tunai harian. Auto-sync dari Revenue dan Expenses untuk real-time cash position."
            keyFeatures={['Daily Tracking', 'Auto-Sync', 'Running Balance', 'Cash Flow Chart', 'Projections']}
            howToUse={[
              "Lihat daily cash movement dalam table",
              "Opening Balance = Baki awal hari (auto dari previous day closing)",
              "Cash In = Total masuk hari tu (from Revenue)",
              "Cash Out = Total keluar hari tu (from Expenses + Payroll)",
              "Closing Balance = Opening + In - Out",
              "Net Flow = Cash In - Cash Out (positive = good)",
              "Boleh manual adjust Opening Balance jika perlu reconcile dengan bank",
              "Chart shows trend over time"
            ]}
            tips={[
              "Reconcile dengan bank statement weekly",
              "Negative net flow berturut-turut = warning sign",
              "Maintain minimum 3 months operating expenses as buffer",
              "Check closing balance matches actual bank balance"
            ]}
          />

          <MenuCard
            title="Channel Profitability"
            icon={PieChart}
            path="/channel-profitability"
            description="Track untung/rugi setiap channel. Identify channel mana yang profitable dan mana yang drain resources."
            keyFeatures={['9 Pre-loaded Channels', 'Revenue per Channel', 'Cost per Channel', 'Profit & Margin', 'Visual Rankings']}
            howToUse={[
              "9 channels sudah pre-loaded: FTC, Goldcast, MBE, Ciko, Revolusi Nusatrade, Sahara AI, MSE Ustad, Garuda Global, Padei",
              "Untuk add revenue: Klik 'Tambah Revenue' → pilih channel → masukkan amount & date",
              "Untuk add cost: Klik 'Tambah Cost' → pilih channel → masukkan kos & category",
              "System auto-calculate: Profit = Revenue - Cost, Margin = Profit/Revenue × 100%",
              "View 'Top 5 Profitable' untuk best performing channels",
              "View 'Channels Yang Rugi' untuk channels yang need attention",
              "Klik channel name untuk detailed breakdown"
            ]}
            tips={[
              "Track SETIAP transaction by channel untuk accurate analysis",
              "Margin <15% = need review (pricing or cost structure)",
              "Losing channels = consider discontinue or renegotiate terms",
              "Focus resources on high-margin channels"
            ]}
          />

          <MenuCard
            title="Profit & Loss"
            icon={Target}
            path="/profit-loss"
            description="Laporan Untung Rugi standard dengan breakdown by category. Essential untuk financial reporting."
            keyFeatures={['Standard P&L Format', 'Category Breakdown', 'Period Comparison', 'Gross vs Net', 'Export Function']}
            howToUse={[
              "Pilih period: Monthly / Quarterly / Yearly / Custom Range",
              "Section 1: Revenue - breakdown by source",
              "Section 2: Cost of Sales - direct costs",
              "Section 3: Gross Profit = Revenue - Cost of Sales",
              "Section 4: Operating Expenses - by category",
              "Section 5: Net Profit = Gross Profit - Operating Expenses",
              "Compare dengan previous period untuk trend",
              "Export untuk accountant atau audit"
            ]}
            tips={[
              "Healthy gross margin varies by industry (aim 40-60% for services)",
              "Net margin 15-25% is healthy for SME",
              "If net margin <10%, review expenses urgently",
              "Compare YoY untuk eliminate seasonal variations"
            ]}
          />

          <MenuCard
            title="Balance Sheet"
            icon={Building2}
            path="/balance-sheet"
            description="Penyata Kedudukan Kewangan - Assets, Liabilities, dan Equity pada satu masa."
            keyFeatures={['Assets Breakdown', 'Liabilities List', 'Equity Calculation', 'Snapshot View']}
            howToUse={[
              "Assets = Apa yang syarikat MILIKI (Cash, Equipment, Property, Receivables)",
              "Liabilities = Apa yang syarikat HUTANG (Loans, Payables, Accruals)",
              "Equity = Assets - Liabilities (nilai bersih syarikat)",
              "View breakdown untuk each category",
              "Compare dengan previous period"
            ]}
            tips={[
              "Assets > Liabilities = Healthy (positive equity)",
              "Current Ratio (Current Assets / Current Liabilities) should be >1.5",
              "Review quarterly atau bila ada major transactions"
            ]}
          />

          <MenuCard
            title="Monthly Summary"
            icon={FileText}
            path="/monthly-summary"
            description="Ringkasan bulanan semua metrics kewangan dalam satu page untuk easy review."
            keyFeatures={['All KPIs in One View', 'Month Selection', 'Comparison', 'Quick Export']}
            howToUse={[
              "Select month dari dropdown",
              "View all key metrics: Revenue, Expenses, Profit, Cash, Receivables, Payables",
              "Compare dengan bulan sebelumnya (auto-shown)",
              "Export untuk monthly report ke management"
            ]}
            tips={[
              "Review pada 1st week of new month",
              "Guna untuk monthly management meeting",
              "Identify trends dan anomalies early"
            ]}
          />

          <MenuCard
            title="Aset"
            icon={Package}
            path="/assets"
            description="Manage semua aset syarikat - equipment, kenderaan, property. Auto-calculate depreciation."
            keyFeatures={['Asset Register', 'Auto Depreciation', 'Maintenance Tracking', 'Warranty Alerts', 'Asset Categories']}
            howToUse={[
              "Klik 'Tambah Aset' untuk register new asset",
              "Isi: Name, Category (Equipment/Vehicle/Property/etc), Purchase Date, Purchase Value",
              "Set Useful Life (years) untuk depreciation calculation",
              "System auto-calculate: Annual Depreciation, Current Value, Accumulated Depreciation",
              "Set Maintenance Schedule - system akan alert bila due",
              "Enter Warranty Expiry - get alerts before expiry",
              "Attach purchase documents untuk record"
            ]}
            tips={[
              "Register ALL assets including small items (for insurance purposes)",
              "Update maintenance records setiap kali service done",
              "Check warranty expiry alerts monthly",
              "Depreciation affects P&L - ensure accurate values"
            ]}
          />
        </GuideSection>
      </div>

      {/* HR Menu */}
      <div id="hr">
        <GuideSection title="HR & OPERATIONS" icon={Briefcase} color="blue">
          <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>Untuk siapa?</strong> HR Team, Operations Manager, Directors
            </p>
            <p className="text-sm text-blue-700 mt-1">
              <strong>Tujuan:</strong> Manage staff, payroll, attendance, leave, performance, dan hiring decisions
            </p>
          </div>

          <MenuCard
            title="Human Resources"
            icon={UserCog}
            path="/hr"
            description="Pusat pengurusan HR dengan modul lengkap: Attendance, Leave, Performance, Disciplinary, Training."
            keyFeatures={['5 HR Modules', 'Attendance Import', 'Leave Workflow', 'Performance Rating', 'Training Records']}
            howToUse={[
              "Tab ATTENDANCE: Import attendance dari CSV/Excel atau key in manual. Column: Staff, Date, Time In, Time Out",
              "Tab LEAVE: View pending requests → Approve/Reject dengan satu klik. Staff akan nampak updated status",
              "Tab PERFORMANCE: Rate staff 1-5 stars. Add comments. Review history",
              "Tab DISCIPLINARY: Record warnings. Track status (Active/Resolved). Set follow-up dates",
              "Tab TRAINING: Log training attended. Track certificates. Plan future training"
            ]}
            tips={[
              "Process leave requests dalam 24 jam",
              "Do performance review setiap quarter (minimum)",
              "Document all disciplinary actions untuk protection",
              "Track mandatory training compliance"
            ]}
          />

          <MenuCard
            title="Staff & Payroll"
            icon={Users}
            path="/staff-payroll"
            description="Database staff lengkap dengan auto-calculation untuk EPF, SOCSO, EIS, dan PCB."
            keyFeatures={['Staff Database', 'Auto Statutory Calc', 'Payslip Generation', 'Salary History', 'Department View']}
            howToUse={[
              "Klik 'Tambah Staff' untuk add new employee",
              "Isi: Full Name, IC Number, Position, Department, Join Date, Basic Salary",
              "System AUTO-CALCULATE: EPF (Employee 11%, Employer 13%), SOCSO, EIS, PCB",
              "View: Gross Salary, Total Deductions, Net Salary, Total Employer Cost",
              "Edit untuk update salary, position, atau details",
              "Filter by Department atau Status (Active/Resigned)",
              "Total Payroll shows at header untuk budgeting"
            ]}
            tips={[
              "IC Number must be correct - affects SOCSO/PCB calculation",
              "Update salary changes immediately untuk accurate payroll",
              "Total Employer Cost = what company actually pays (include EPF employer, SOCSO, EIS)",
              "Review payroll costs against revenue monthly"
            ]}
          />

          <MenuCard
            title="Staff Analytics"
            icon={Activity}
            path="/staff-analytics"
            description="Advanced analytics: ROI per staff, productivity trends, dan flight risk prediction."
            keyFeatures={['Staff ROI', 'Productivity Score', 'Flight Risk Indicator', '6-Month Trends', 'Recommendations']}
            howToUse={[
              "TAB ROI ANALYSIS: View revenue contribution vs cost per staff",
              "ROI = (Revenue Contribution - Staff Cost) / Staff Cost × 100%",
              "Productivity Score 0-100 based on efficiency metrics",
              "View Top Performers dan Needs Attention lists",
              "TAB FLIGHT RISK: Risk score 0-100 for each staff",
              "Risk factors shown: tenure, salary vs average, warnings, no promotion",
              "TAB PRODUCTIVITY TREND: 6-month chart comparing revenue vs payroll"
            ]}
            tips={[
              "High ROI staff = prioritize retention (increment, promotion)",
              "High flight risk = conduct stay interview, address concerns",
              "Negative ROI bukan bermakna staff tak bagus - review workload dan tools",
              "Use trends untuk forecast hiring needs"
            ]}
          />

          <MenuCard
            title="Staff Details & Personality"
            icon={Brain}
            path="/staff-details"
            description="Complete staff personality assessment dengan VAKD, DOPE, dan Colour Brain. Membantu leader faham cara terbaik untuk communicate, delegate, dan motivate setiap staff."
            keyFeatures={['Team Health Score', 'VAKD Assessment', 'DOPE Analysis', 'Colour Brain', 'Smart Insights', 'Task Optimizer', 'Welfare Alerts']}
            howToUse={[
              "TAB SMART INSIGHTS: View Welfare Alerts (staff stress handling rendah), Task Assignment Optimizer, Team Dynamics Analysis",
              "TAB TEAM ANALYTICS: View Team Health Score (0-100), VAKD/DOPE/Colour Brain distribution charts",
              "TAB SENARAI STAFF: Click staff card untuk view details, Click edit untuk update personality data",
              "EDIT FORM - 7 Sections:",
              "  • Peribadi: IC, DOB, Gender, Race, Religion",
              "  • Perhubungan: Phone, Email, Address, Emergency Contact",
              "  • Pekerjaan: Employee ID, Department, Position, Bank Info",
              "  • Pendidikan: Education Level, Field, Languages",
              "  • Personaliti: Communication Style, Work Style, Leadership Potential, Strengths",
              "  • Colour Brain: Red (Action), Blue (Analytical), Green (Creative), Yellow (People)",
              "  • VAKD & DOPE: Learning style dan behavior style",
              "Task Optimizer: Pilih jenis task → System suggest staff terbaik berdasarkan personality"
            ]}
            tips={[
              "Team Health Score >70 = Team sehat. <40 = Perlu attention segera",
              "VAKD: Faham cara setiap staff TERIMA maklumat",
              "  • Visual: Tunjuk dengan slides/diagram",
              "  • Audio: Explain secara lisan",
              "  • Kinestetik: Demo dan hands-on",
              "  • Digital: Bagi data dan reasoning",
              "DOPE: Faham cara setiap staff BUAT KEPUTUSAN",
              "  • Dove 🕊️: Supportive, avoid conflict → Bagi appreciation",
              "  • Owl 🦉: Detail-oriented → Bagi data complete",
              "  • Peacock 🦚: Creative, people person → Bagi recognition",
              "  • Eagle 🦅: Results-driven → Bagi autonomy",
              "Colour Brain: Faham cara setiap staff BERFIKIR",
              "  • Red 🔴: Fast, decisive → Direct communication",
              "  • Blue 🔵: Analytical → Bagi time untuk analyze",
              "  • Green 🟢: Creative → Bagi freedom untuk experiment",
              "  • Yellow 🟡: People-focused → Build relationship first",
              "Guna Task Optimizer bila assign project untuk best results",
              "Check Welfare Alerts weekly - address staff stress early"
            ]}
          />

          <MenuCard
            title="Hiring Decision"
            icon={UserPlus}
            path="/hiring"
            description="Data-driven dashboard untuk decide: Hire atau tidak? Simulate impact of new hires."
            keyFeatures={['Current Metrics', 'Industry Benchmark', 'Hire Simulation', 'AI Recommendation', 'Cost Projection']}
            howToUse={[
              "View current: Headcount, Payroll Cost, Revenue per Employee",
              "Compare dengan Industry Benchmark (RM5,000 revenue/employee typical)",
              "Check Workload Indicators: Overtime trend, Capacity utilization",
              "Use Simulator: Enter proposed salary → See impact on metrics",
              "Read AI Recommendation berdasarkan current data",
              "Decision factors: Revenue trend, Cash position, Workload, Growth plans"
            ]}
            tips={[
              "Hire bila revenue/employee > benchmark AND positive cash flow",
              "Factor in 3-6 months training period (reduced productivity)",
              "Consider contract/freelance jika uncertain about long-term need",
              "Review quarterly - hiring needs change dengan business"
            ]}
          />
        </GuideSection>
      </div>

      {/* Data Integration Info */}
      <div id="integration" className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
        <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-emerald-400" /> Data Integration - Semua Connected!
        </h2>
        <p className="text-gray-300 text-sm mb-4">
          Sistem ini <strong>TIADA DATA ISOLATION</strong>. Semua module connected dan data flow automatically antara satu sama lain.
        </p>
        
        <div className="grid md:grid-cols-3 gap-4 text-sm mb-6">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Revenue Sources:
            </p>
            <ul className="text-gray-300 space-y-1 text-xs">
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Main Revenue → Total Revenue</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Channel Revenue → Total Revenue</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Bank Import (credit) → Revenue</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> <strong>SQL Sales Invoice → Revenue</strong></li>
            </ul>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-red-400 font-medium mb-2 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Expense Sources:
            </p>
            <ul className="text-gray-300 space-y-1 text-xs">
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Main Expenses → Total Expenses</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Channel Costs → Total Expenses</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Staff Payroll → Total Expenses</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Bank Import (debit) → Expenses</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> <strong>SQL Purchase Invoice → Expenses</strong></li>
            </ul>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Auto-Sync:
            </p>
            <ul className="text-gray-300 space-y-1 text-xs">
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> Revenue + Expenses → P&L</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> All transactions → Cashflow</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> All data → AI Analysis</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> All data → Command Center</li>
              <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3" /> <strong>SQL Accounting → Real-time Sync</strong></li>
            </ul>
          </div>
        </div>

        <div className="bg-white/5 rounded-lg p-4">
          <p className="text-sm text-gray-300">
            <strong className="text-white">Maksudnya:</strong> Key in data sekali sahaja. Sistem akan auto-update semua dashboard, reports, dan analytics. Tiada perlu double-entry atau manual sync.
          </p>
        </div>
      </div>

      {/* SQL Accounting Section - NEW */}
      <div id="sql-accounting" className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-cyan-600" /> SQL Accounting Integration (BARU!)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Sistem ini terintegrasi dengan <strong>SQL Accounting</strong> untuk real-time data sync. Data dari SQL Accounting akan auto-populate ke Dashboard.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-cyan-600" /> Apa yang di-Sync?
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Sales Invoices → Revenue</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Purchase Invoices → Expenses</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Customers → Customer Analytics</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Suppliers → Supplier List</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Stock Items → Inventory Analytics</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> GL Accounts → Chart of Accounts</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-blue-600" /> Cara Guna
            </h4>
            <ol className="text-sm text-gray-600 space-y-1">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">1</span>
                Pergi ke Menu <strong>SQL Accounting → Overview & Sync</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">2</span>
                Klik butang <strong>&quot;Sync Now&quot;</strong> untuk refresh data
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">3</span>
                Data akan auto-populate ke Dashboard modules
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">4</span>
                View <strong>Customer Analytics</strong> & <strong>Inventory Analytics</strong> untuk insights
              </li>
            </ol>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <p className="font-medium text-gray-800 text-sm">Customer Analytics</p>
            <p className="text-xs text-gray-500">Outstanding balance, credit limit, top customers</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <p className="font-medium text-gray-800 text-sm">Inventory Analytics</p>
            <p className="text-xs text-gray-500">Stock value, low stock alerts, out of stock items</p>
          </div>
        </div>
      </div>

      {/* Security & Roles Section - NEW */}
      <div id="security" className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-red-600" /> Security & User Roles (BARU!)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Sistem menggunakan <strong>Google OAuth</strong> untuk login. Setiap user ada role dengan akses berbeza.
        </p>
        
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-amber-500">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-amber-500" />
              <h4 className="font-bold text-gray-800">DIRECTOR</h4>
            </div>
            <p className="text-xs text-gray-600 mb-2">Akses penuh ke semua features</p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>✓ Executive Dashboard</li>
              <li>✓ Finance (Semua)</li>
              <li>✓ HR (Semua)</li>
              <li>✓ SQL Accounting</li>
              <li>✓ AI Analysis</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-emerald-500">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              <h4 className="font-bold text-gray-800">FINANCE</h4>
            </div>
            <p className="text-xs text-gray-600 mb-2">Akses penuh ke semua features</p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>✓ Executive Dashboard</li>
              <li>✓ Finance (Semua)</li>
              <li>✓ HR (Semua)</li>
              <li>✓ SQL Accounting</li>
              <li>✓ AI Analysis</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-500" />
              <h4 className="font-bold text-gray-800">HR</h4>
            </div>
            <p className="text-xs text-gray-600 mb-2">Akses HR sahaja</p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>✓ Human Resources</li>
              <li>✓ Staff & Payroll</li>
              <li>✓ Staff Analytics</li>
              <li>✓ Hiring Decision</li>
              <li>✓ Panduan</li>
              <li className="text-red-400">✗ Finance, Executive, SQL</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-semibold text-gray-800 mb-2">Cara Login</h4>
          <ol className="text-sm text-gray-600 space-y-1">
            <li>1. Buka aplikasi → akan redirect ke Login page</li>
            <li>2. Klik <strong>&quot;Log masuk dengan Google&quot;</strong></li>
            <li>3. Pilih akaun Google yang didaftarkan</li>
            <li>4. Selepas login, menu akan dipaparkan berdasarkan role anda</li>
          </ol>
          <div className="mt-3 p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              <strong>Nota:</strong> Hanya email yang didaftarkan dalam sistem boleh login. Hubungi admin untuk tambah user baru.
            </p>
          </div>
        </div>
      </div>

      {/* Common Actions Quick Reference */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-gray-600" /> Common Actions - Quick Reference
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Plus className="w-4 h-4 text-emerald-600" />
              <span className="font-medium">Add New</span>
            </div>
            <p className="text-xs text-gray-600">Klik button &quot;Tambah&quot; atau &quot;+&quot; di setiap page</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Edit className="w-4 h-4 text-blue-600" />
              <span className="font-medium">Edit</span>
            </div>
            <p className="text-xs text-gray-600">Klik icon pensil di row yang nak edit</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-4 h-4 text-red-600" />
              <span className="font-medium">Delete</span>
            </div>
            <p className="text-xs text-gray-600">Klik icon tong sampah. Confirm untuk padam</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-4 h-4 text-purple-600" />
              <span className="font-medium">Export</span>
            </div>
            <p className="text-xs text-gray-600">Klik &quot;Export&quot; untuk download CSV/Excel</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-amber-600" />
              <span className="font-medium">Filter</span>
            </div>
            <p className="text-xs text-gray-600">Guna dropdown atau search box untuk filter data</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-cyan-600" />
              <span className="font-medium">View Details</span>
            </div>
            <p className="text-xs text-gray-600">Klik row atau icon mata untuk details</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-4 h-4 text-gray-600" />
              <span className="font-medium">Refresh</span>
            </div>
            <p className="text-xs text-gray-600">Klik refresh icon atau press Ctrl+R</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-pink-600" />
              <span className="font-medium">Date Range</span>
            </div>
            <p className="text-xs text-gray-600">Pilih period dari dropdown di header</p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-blue-600" /> Soalan Lazim (FAQ) - 25 Soalan
        </h2>
        
        {/* FAQ Filter */}
        <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b">
          <button 
            onClick={() => setFaqFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Semua ({faqs.length})
          </button>
          <button 
            onClick={() => setFaqFilter('general')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'general' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Umum (5)
          </button>
          <button 
            onClick={() => setFaqFilter('finance')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'finance' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
          >
            Kewangan (7)
          </button>
          <button 
            onClick={() => setFaqFilter('hr')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'hr' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
          >
            HR (6)
          </button>
          <button 
            onClick={() => setFaqFilter('technical')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'technical' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`}
          >
            Teknikal (8)
          </button>
          <button 
            onClick={() => setFaqFilter('troubleshoot')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${faqFilter === 'troubleshoot' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
          >
            Masalah (5)
          </button>
        </div>
        
        {/* FAQ List */}
        <div className="space-y-1">
          {filteredFaqs.map((faq, index) => (
            <FAQItem key={index} {...faq} />
          ))}
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5" /> Keyboard Shortcuts
        </h2>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Ctrl + R</kbd>
            <span className="text-gray-600">Refresh page</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Ctrl + F</kbd>
            <span className="text-gray-600">Search in page</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Ctrl + P</kbd>
            <span className="text-gray-600">Print page</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Tab</kbd>
            <span className="text-gray-600">Move to next field</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Enter</kbd>
            <span className="text-gray-600">Submit form</span>
          </div>
          <div className="flex items-center gap-3">
            <kbd className="px-2 py-1 bg-white border rounded shadow-sm font-mono text-xs">Esc</kbd>
            <span className="text-gray-600">Close modal/popup</span>
          </div>
        </div>
      </div>

      {/* Version Info */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">UA Syarikat — Sistem Pengurusan Kewangan & HR</h3>
            <p className="text-sm text-gray-500 mt-1">Version 2.0 • Underads Sdn Bhd</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Last Updated</p>
            <p className="font-medium text-gray-800">December 2024</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Panduan ini mengandungi 4 kategori menu, 20+ modules, dan 25 FAQ. 
            Anggaran masa baca: 15-20 minit. Selepas baca, anda sepatutnya faham 80% cara guna sistem.
          </p>
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 text-center">
        <AlertCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
        <p className="text-blue-800 font-medium">Ada soalan lain yang tidak terjawab?</p>
        <p className="text-blue-600 text-sm mt-1">Hubungi admin atau IT support untuk bantuan lanjut.</p>
        <p className="text-blue-500 text-xs mt-2">Atau rujuk Director untuk keputusan berkaitan polisi syarikat.</p>
      </div>
    </div>
  );
};

export default GuidePage;
